import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import api, { getErrorMessage } from '@/hooks/useApi';

// Types matching backend responses
export interface StoryListItem {
  storyId: string;
  genre: string;
  title: string;
  description: string;
  status: string;
  totalNodes: number;
  totalPlays: number;
  createdAt: string;
}

export interface StoryDetails {
  storyId: string;
  genre: string;
  title: string;
  description: string;
  rootNodeId: string;
  status: string;
  totalNodes: number;
  totalPlays: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateStoryInput {
  genre: string;
  title: string;
  description: string;
  initialState: {
    setting: string;
    protagonist: { name: string; traits: string[] };
    currentTension: string;
  };
}

interface ListStoriesResponse {
  stories: StoryListItem[];
}

interface CreateStoryResponse {
  storyId: string;
  message: string;
}

// Query keys for cache management
export const storyKeys = {
  all: ['stories'] as const,
  lists: () => [...storyKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...storyKeys.lists(), filters] as const,
  details: () => [...storyKeys.all, 'detail'] as const,
  detail: (id: string) => [...storyKeys.details(), id] as const,
};

/**
 * Fetch all stories (admin endpoint shows all stories regardless of status)
 */
export function useStories(statusFilter?: string) {
  return useQuery({
    queryKey: storyKeys.list({ status: statusFilter }),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter) {
        params.set('status', statusFilter);
      }
      // Use admin endpoint that returns all stories regardless of status
      const response = await api.get<ListStoriesResponse>(`/admin/stories?${params.toString()}`);
      return response.data.stories;
    },
  });
}

/**
 * Fetch a single story by ID
 */
export function useStory(storyId: string | undefined) {
  return useQuery({
    queryKey: storyKeys.detail(storyId ?? ''),
    queryFn: async () => {
      const response = await api.get<StoryDetails>(`/stories/${storyId}`);
      return response.data;
    },
    enabled: !!storyId,
  });
}

/**
 * Create a new story
 */
export function useCreateStory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateStoryInput) => {
      const response = await api.post<CreateStoryResponse>('/admin/stories', input);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: storyKeys.lists() });
    },
    onError: (error) => {
      // Error is available via mutation.error - no return needed
      // Logging here for debugging; UI should display mutation.error
      console.error('Create story failed:', getErrorMessage(error));
    },
  });
}
